<template>
  <div class="projects-page" v-loading="loading">
    <div class="projects-heading">
      <div><div class="eyebrow">DEEPCITY · AI TEAM</div><h1>AI 项目团队</h1>
        <p>把目标、员工和资料放在一起，让不同专长共同完成一个项目。</p></div>
      <el-button type="primary" icon="el-icon-plus" @click="openCreate">创建项目</el-button>
    </div>
    <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
    <div class="project-grid">
      <div v-for="project in projects" :key="project.projectGuid" class="project-card">
        <button class="project-card-main" :disabled="deletingGuid === project.projectGuid" @click="openProject(project)">
          <div class="project-icon"><i class="el-icon-connection" /></div>
          <h3>{{ project.name }}</h3><p>配置版本 {{ project.configVersion }} · {{ project.status === 'active' ? '可配置' : '已暂停' }}</p>
          <span>查看团队与项目配置 <i class="el-icon-arrow-right" /></span>
        </button>
        <el-button class="project-card-delete" type="text" icon="el-icon-delete" :aria-label="`删除项目：${project.name}`"
          :disabled="!!deletingGuid || saving" @click.stop="removeProject(project)">删除</el-button>
      </div>
    </div>
    <el-empty v-if="!loading && !projects.length" description="创建第一个项目，组建你的 AI 团队" />
    <section v-if="selected" class="project-overview">
      <div class="overview-heading"><h2>{{ selected.name }}</h2><div>
        <el-button type="danger" plain :disabled="!!deletingGuid || saving" @click="removeProject(selected)">删除项目</el-button>
        <el-button :disabled="!!deletingGuid" @click="openEdit">编辑项目配置</el-button></div></div>
      <el-tabs v-model="activeTab">
        <el-tab-pane :label="options.workspaceEnabled ? '主管对话' : '团队任务'" name="tasks">
          <project-conversation-panel v-if="options.workspaceEnabled" :project="selected" :workspace-enabled="true" @open-task="openTeamTask" @download="downloadAsset" @create-automation="openAutomation" />
          <project-task-panel v-else ref="tasks" :project-guid="selected.projectGuid" :assets="assets"
          @assets-changed="loadAssets" @download="downloadAsset" @create-automation="openAutomation" /></el-tab-pane>
        <el-tab-pane v-if="!options.workspaceEnabled" label="主管对话记录" name="conversationHistory">
          <project-conversation-panel :project="selected" :workspace-enabled="false" @open-task="openTeamTask" @download="downloadAsset" />
        </el-tab-pane>
        <el-tab-pane label="定时任务" name="automation"><project-automation-panel ref="automation" :project-guid="selected.projectGuid" @open-task="openTeamTask" /></el-tab-pane>
        <el-tab-pane label="事项看板" name="plan"><project-plan-panel :project-guid="selected.projectGuid" /></el-tab-pane>
        <el-tab-pane label="项目目标" name="goal"><p class="project-instructions">{{ selected.instructions }}</p></el-tab-pane>
        <el-tab-pane label="AI 员工" name="members">
          <div v-for="member in selected.members" :key="member.memberGuid" class="member-row">
            <strong>{{ memberLabel(member) }}</strong><p>{{ member.roleInProject }}</p>
          </div>
        </el-tab-pane>
        <el-tab-pane label="Skills" name="skills"><el-tag v-for="skill in selected.capabilityBindings" :key="skill.capabilityId">{{ skillName(skill) }}</el-tag>
          <p v-if="!selected.capabilityBindings.length">尚未选择项目 Skill，可在编辑配置中添加。</p></el-tab-pane>
        <el-tab-pane label="共享资料" name="assets">
          <el-upload action="" :http-request="uploadAsset" :show-file-list="false" :disabled="assetUploading"
            accept=".txt,.md,.csv,.pdf,.docx,.xlsx,.pptx"><el-button type="primary" :loading="assetUploading" icon="el-icon-upload2">上传共享资料</el-button></el-upload>
          <p class="form-hint">项目员工共同使用这些资料。文件按版本保存，只有项目创建者可以查看和下载。</p>
          <el-table :data="assets" style="width:100%">
            <el-table-column prop="displayName" label="资料名称" /><el-table-column prop="version" label="版本" width="70" />
            <el-table-column prop="sourceKind" label="来源" width="100" /><el-table-column label="操作" min-width="240">
              <template slot-scope="scope"><el-button type="text" @click="downloadAsset(scope.row)">下载</el-button>
                <project-media-preview :project-guid="selected.projectGuid" :file="scope.row" />
                <el-upload action="" :http-request="args => uploadAsset(args, scope.row.assetGuid)" :show-file-list="false" class="inline-upload">
                  <el-button type="text">上传新版</el-button></el-upload></template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </section>
    <el-drawer title="项目任务记录" :visible.sync="taskHistoryVisible" size="80%" append-to-body @opened="openHistoryTarget">
      <div v-if="selected && taskHistoryVisible" style="padding:20px">
        <project-task-panel ref="taskHistory" :project-guid="selected.projectGuid" :assets="assets"
          @assets-changed="loadAssets" @download="downloadAsset" @create-automation="openAutomation" />
      </div>
    </el-drawer>
    <el-dialog :title="draft.projectGuid ? '编辑 AI 项目' : '组建你的 AI 项目团队'" :visible.sync="formVisible" width="780px" :close-on-click-modal="false">
      <el-form label-position="top" class="project-form">
        <el-form-item label="项目名称"><el-input v-model="draft.name" maxlength="80" show-word-limit placeholder="例如：海外店铺运营" /></el-form-item>
        <el-form-item label="项目目标与背景"><el-input v-model="draft.instructions" type="textarea" :rows="4" placeholder="说明项目目标、资料范围、交付要求和团队共同遵守的规则" /></el-form-item>
        <el-form-item label="团队统一模型">
          <el-select :value="modelKey(draft.defaultModelRef)" :disabled="modelVerifying" filterable placeholder="请选择平台模型或已验证的个人模型" @change="selectModel">
            <el-option v-for="model in options.models" :key="modelKey(model.modelRef)" :value="modelKey(model.modelRef)" :label="`${model.source === 'personal' ? '个人模型' : '平台模型'} · ${model.name}`" />
          </el-select><el-button type="text" @click="$router.push('/app/personal-models')">管理我的个人模型</el-button>
          <el-button :loading="modelVerifying" :disabled="!draft.defaultModelRef" @click="verifySelectedModel">验证主管能力</el-button>
          <p class="form-hint">检测文字、工具和计划协议，最长3分钟，可能产生少量模型调用费用；通过结果有效5分钟。</p>
          <div v-if="modelVerification" class="model-verification" aria-live="polite">
            <strong>{{ modelVerification.status === 'passed' ? '主管能力验证通过' : '本次检测未通过，请检查模型配置或稍后重试' }}</strong>
            <div><el-tag v-for="check in modelVerificationChecks" :key="check.key" :type="modelVerification.checks && modelVerification.checks[check.key] ? 'success' : 'info'">
              {{ check.label }}：{{ modelVerification.checks && modelVerification.checks[check.key] ? '通过' : '未通过' }}
            </el-tag></div>
          </div>
          <p class="form-hint">所有员工使用项目所选模型；选择员工不会共享其创建者的 Key。</p>
        </el-form-item>
        <el-form-item label="选择 AI 员工与项目职责">
          <el-select v-model="selectedMemberKeys" multiple filterable placeholder="复用我的内部智能体或文案员工" @change="selectMembers">
            <el-option v-for="member in options.members" :key="memberKey(member)" :value="memberKey(member)" :label="`${member.sourceKind === 'internalAgent' ? '我的智能体' : '文案员工'} · ${member.name}`" />
          </el-select>
          <div v-for="member in draft.members" :key="memberKey(member)" class="member-role">
            <strong>{{ memberLabel(member) }}</strong><el-input v-model="member.roleInProject" maxlength="2000" placeholder="这位员工负责什么，预期交付什么" />
          </div>
          <p class="form-hint">高级数字员工暂不加入项目。先保存团队配置，后续在项目任务中协作。</p>
        </el-form-item>
        <el-form-item label="项目允许使用的 Skills">
          <agent-capability-picker v-model="draft.capabilityBindings" :catalog="options.skills.list || []"
            :options-by-id="capabilityOptions" @request-options="loadCapabilityOptions" />
        </el-form-item>
      </el-form>
      <span slot="footer"><el-button @click="formVisible = false">取消</el-button><el-button type="primary" :disabled="modelVerifying" :loading="saving" @click="save">保存项目配置</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import AgentCapabilityPicker from '../layout-agent/components/agent-capability-picker.vue'
import ProjectTaskPanel from './project-task-panel.vue'
import ProjectAutomationPanel from './project-automation-panel.vue'
import ProjectPlanPanel from './project-plan-panel.vue'
import ProjectConversationPanel from './project-conversation-panel.vue'
import { getAgentCapabilityOptionsApi } from '@/apis/agent'
import { listProjects, projectOptions, projectDetail, createProject, updateProject,
  projectAssets, uploadProjectAsset, downloadProjectAsset, verifyProjectModel, deleteProject } from '@/apis/project-team'
import { buildProjectPayload, projectDraftFromDetail, assertProjectModelVerification } from '@/utils/project-team-form.mjs'
import { requireChatResponse } from '@/utils/chat-response.mjs'
import ProjectMediaPreview from './project-media-preview.vue'

export default {
  name: 'ProjectTeams', components: { AgentCapabilityPicker, ProjectTaskPanel, ProjectAutomationPanel, ProjectPlanPanel, ProjectConversationPanel, ProjectMediaPreview },
  data: () => ({ projects: [], selected: null, activeTab: 'goal', loading: false, saving: false,
    error: '', formVisible: false, draft: projectDraftFromDetail(), selectedMemberKeys: [],
    options: { members: [], models: [], skills: { list: [] }, workspaceEnabled: false }, capabilityOptions: {}, assets: [], assetUploading: false,
    taskHistoryVisible: false, historyTarget: null, originalModelRef: null, deletingGuid: '', projectOpenSequence: 0, openingProjectGuid: '',
    modelVerifying: false, modelVerification: null, modelVerificationSequence: 0,
    modelVerificationChecks: [{ key: 'text', label: '文字回应' }, { key: 'toolCall', label: '工具查询' },
      { key: 'plan', label: '串行计划' }, { key: 'roundtrip', label: '工具回包回应' }] }),
  async created() { await this.load(); const project = this.projects.find(p => p.projectGuid === this.$route.query.projectGuid); if (project) this.openProject(project) },
  methods: {
    openAutomation(runGuid) { this.taskHistoryVisible=false;this.activeTab='automation';this.$nextTick(()=>this.$refs.automation.openCreate(runGuid)) },
    openTeamTask(taskGuid) {
      this.activeTab = 'tasks'
      if (this.options.workspaceEnabled) { this.historyTarget = taskGuid || null; this.taskHistoryVisible = true }
      else if (taskGuid) this.$nextTick(() => this.$refs.tasks.selectTask({ taskGuid }))
    },
    openHistoryTarget() { if (this.historyTarget && this.$refs.taskHistory) this.$refs.taskHistory.selectTask({ taskGuid: this.historyTarget }) },
    modelKey(ref) { return ref ? `${ref.source}:${ref.modelGuid}` : '' },
    memberKey(member) { return `${member.sourceKind}:${member.sourceGuid}` },
    memberLabel(member) { return this.options.members.find(item => this.memberKey(item) === this.memberKey(member))?.name || member.roleInProject },
    skillName(skill) { return this.options.skills.list?.find(item => item.capabilityId === skill.capabilityId)?.name || skill.capabilityId },
    async load() {
      this.loading = true; this.error = ''
      try { const [rows, options] = await Promise.all([listProjects(), projectOptions()]); this.projects = rows.list; this.options = options }
      catch (error) { this.error = error.message } finally { this.loading = false }
    },
    async openProject(project) {
      if (this.deletingGuid === project.projectGuid) return
      const sequence = ++this.projectOpenSequence; this.openingProjectGuid = project.projectGuid
      try {
        const wanted = this.$route.query.projectGuid === project.projectGuid ? this.$route.query.taskGuid : null
        const detail = await projectDetail(project.projectGuid)
        // 删除或切换项目后的迟到响应，不能重新打开已经离开的项目。
        if (sequence !== this.projectOpenSequence || !this.projects.some(item => item.projectGuid === project.projectGuid)) return
        this.selected = detail; this.activeTab = 'tasks'; await this.loadAssets()
        if (sequence !== this.projectOpenSequence) return
        this.$router.replace({ path: this.$route.path, query: { ...this.$route.query, projectGuid: project.projectGuid, taskGuid: wanted || undefined } }).catch(() => {})
        if (wanted && this.options.workspaceEnabled) this.openTeamTask(wanted)
      }
      catch (error) { if (sequence === this.projectOpenSequence) this.$message.error(error.message) }
      finally { if (sequence === this.projectOpenSequence) this.openingProjectGuid = '' }
    },
    async loadAssets() {
      if (!this.selected) return
      const guid = this.selected.projectGuid; const result = await projectAssets(guid)
      if (this.selected?.projectGuid === guid) this.assets = result.list
    },
    async removeProject(project) {
      if (this.deletingGuid || this.saving) return
      const guid = project.projectGuid; this.deletingGuid = guid
      try {
        await this.$confirm(`删除“${project.name}”后，项目将从列表移除，定时任务停止。历史文件与费用记录会保留。有未完成工作时，请先结束工作。`,
          '删除项目', { type: 'warning', confirmButtonText: '确认删除', cancelButtonText: '取消', closeOnClickModal: false })
        const result = await deleteProject({ projectGuid: guid, configVersion: project.configVersion })
        if (result?.deleted !== true || result.projectGuid !== guid) throw new Error('删除结果未确认，请刷新后重试')
        this.projects = this.projects.filter(item => item.projectGuid !== guid)
        if (this.openingProjectGuid === guid || this.selected?.projectGuid === guid) {
          this.projectOpenSequence++; this.openingProjectGuid = ''
        }
        if (this.selected?.projectGuid === guid) {
          this.selected = null; this.assets = []; this.taskHistoryVisible = false; this.historyTarget = null
        }
        if (this.draft.projectGuid === guid) this.formVisible = false
        if (this.$route.query.projectGuid === guid) {
          const query = { ...this.$route.query }; delete query.projectGuid; delete query.taskGuid
          this.$router.replace({ path: this.$route.path, query }).catch(() => {})
        }
        this.$message.success('项目已删除')
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || '删除失败，请稍后重试')
      } finally { this.deletingGuid = '' }
    },
    async openCreate() {
      try { this.options = await projectOptions(); this.draft = projectDraftFromDetail(); this.originalModelRef = null
        this.resetModelVerification(); this.selectedMemberKeys = []; this.formVisible = true }
      catch (error) { this.$message.error(error.message) }
    },
    openEdit() {
      this.draft = projectDraftFromDetail(this.selected); this.selectedMemberKeys = this.draft.members.map(this.memberKey); this.formVisible = true
      this.originalModelRef = { ...this.selected.defaultModelRef }; this.resetModelVerification()
      for (const skill of this.draft.capabilityBindings) this.loadCapabilityOptions(skill.capabilityId)
    },
    resetModelVerification() { this.modelVerificationSequence += 1; this.modelVerification = null; this.modelVerifying = false },
    selectModel(key) {
      this.draft.defaultModelRef = this.options.models.find(item => this.modelKey(item.modelRef) === key)?.modelRef || null
      this.resetModelVerification()
    },
    async verifySelectedModel() {
      if (!this.draft.defaultModelRef || this.modelVerifying) return
      const ref = { ...this.draft.defaultModelRef }; const wantedKey = this.modelKey(ref)
      const sequence = ++this.modelVerificationSequence
      this.modelVerifying = true; this.modelVerification = null
      try {
        const result = await verifyProjectModel(ref)
        // 切换表单/模型后的迟到结果不能覆盖当前选择，浏览器始终只持有引用和安全检测状态。
        if (sequence !== this.modelVerificationSequence || wantedKey !== this.modelKey(this.draft.defaultModelRef)) return
        this.modelVerification = { ...result, modelKey: wantedKey }
      } catch (error) {
        if (sequence === this.modelVerificationSequence) this.$message.error(error.message)
      } finally { if (sequence === this.modelVerificationSequence) this.modelVerifying = false }
    },
    selectMembers(keys) {
      this.draft.members = keys.map(key => this.draft.members.find(item => this.memberKey(item) === key)
        || (() => { const source = this.options.members.find(item => this.memberKey(item) === key)
          return { sourceKind: source.sourceKind, sourceGuid: source.sourceGuid, roleInProject: source.name } })())
    },
    async loadCapabilityOptions(capabilityId) {
      if (!['commerce.product.search', 'knowledge.dify.search'].includes(capabilityId)) return
      try { const res = requireChatResponse(await getAgentCapabilityOptionsApi({ capabilityId }), 'Skill配置加载失败'); this.$set(this.capabilityOptions, capabilityId, res.data || []) }
      catch (error) { this.$message.error(error.message) }
    },
    async save() {
      if (this.saving) return
      this.saving = true
      try {
        const payload = buildProjectPayload(this.draft)
        assertProjectModelVerification(this.draft, this.originalModelRef, this.modelVerification)
        const result = await (payload.projectGuid ? updateProject(payload) : createProject(payload))
        this.selected = await projectDetail(result.projectGuid); this.formVisible = false
        this.$message.success('项目配置已保存'); await this.load()
      } catch (error) { this.$message.error(error.message) } finally { this.saving = false }
    },
    async uploadAsset(args, assetGuid) {
      if (!this.selected || this.assetUploading) return
      this.assetUploading = true
      try { await uploadProjectAsset(this.selected.projectGuid, args.file, assetGuid)
        this.assets = (await projectAssets(this.selected.projectGuid)).list; this.$message.success('共享资料已保存')
      } catch (error) { this.$message.error(error.message) } finally { this.assetUploading = false }
    },
    async downloadAsset(asset) {
      try { const result = await downloadProjectAsset({ projectGuid: this.selected.projectGuid,
        assetGuid: asset.assetGuid, version: asset.version })
        const image = /^image\//.test(asset.mimeType || '')
        const url = image ? result.downloadUrl : result.downloadUrl || result.url
        if (!url) throw new Error('图片附件下载暂不可用，请稍后重试')
        const parsed = new URL(url)
        if (parsed.protocol !== 'https:' || parsed.username || parsed.password) throw new Error('资料下载地址无效')
        if (image) {
          const link = document.createElement('a')
          link.href = parsed.href
          link.download = asset.displayName || result.displayName || 'image.png'
          document.body.appendChild(link)
          try { link.click() } finally { link.remove() }
          return
        }
        window.open(parsed.href, '_blank', 'noopener') }
      catch (error) { this.$message.error(error.message) }
    }
  }
}
</script>

<style lang="scss" scoped>
.projects-page { flex: 1; min-width: 0; width: 100%; overflow: auto; height: 100%; box-sizing: border-box; padding: 36px; background: #f8f9fd; }
.projects-heading, .overview-heading { display: flex; align-items: center; justify-content: space-between; gap: 24px; }
.eyebrow { color: #8062f4; font-size: 12px; letter-spacing: 2px; font-weight: 600; }
h1 { font-size: 30px; margin: 12px 0; color: #252b42; } .projects-heading p, .form-hint { color: #8790a4; }
.project-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px; margin: 30px 0; }
.project-card { position: relative; text-align: left; padding: 24px; background: white; border: 1px solid #e6e8f0; border-radius: 16px; transition: .2s; }
.project-card-main { width: 100%; padding: 0; border: 0; background: transparent; text-align: left; cursor: pointer; font: inherit; }
.project-card-main:disabled { cursor: wait; } .project-card-main:focus-visible { outline: 2px solid #8062f4; outline-offset: 6px; }
.project-card-delete { position: absolute; top: 14px; right: 16px; color: #d75454; }
.project-card:hover { border-color: #a28bf7; box-shadow: 0 8px 28px #7962d51a; transform: translateY(-2px); }
.project-icon { color: #8062f4; font-size: 28px; } .project-card h3 { font-size: 18px; color: #293047; }
.project-card p { color: #98a0b2; } .project-card span { color: #8062f4; font-size: 13px; }
.project-overview { padding: 24px; border-radius: 16px; background: white; border: 1px solid #e6e8f0; }
.project-instructions { white-space: pre-wrap; line-height: 1.8; } .member-row { padding: 16px; border-bottom: 1px solid #eee; }
.project-form .el-select { width: 100%; } .member-role { margin-top: 12px; padding: 14px; background: #f8f7ff; border-radius: 8px; }
.member-role strong { display: block; margin-bottom: 8px; } .form-hint { margin: 8px 0 0; font-size: 12px; line-height: 1.6; }
.el-tag { margin: 0 8px 8px 0; }
.model-verification { margin-top: 12px; padding: 12px; background: #f8f7ff; border-radius: 8px; }
.model-verification strong { display: block; margin-bottom: 10px; }
.inline-upload { display: inline-block; margin-left: 10px; }
</style>
